"use client";
import { DataProps } from "..";
import { ParsedFactoid } from "@/components/HeroStats/types";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import MonarchResolver from "@/monarch/MonarchResolver";
import type { HeroStatsTestResult } from "@/monarch/rules/heroStatsRedesign/types";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import { FormProductHeroCombined } from "./FormProductHeroCombined";
import {
  findCustomFactoidData,
  MARYVILLE_PARTNER_UUID,
  processDegree51Data,
} from "@/components/DegreeDetail/DegreeDetailLayout/DegreePageDesign51";
import useTranslations from "@/i18n/useTranslations";

function partnersIncludeMaryville(partners: DataProps["partners"]): boolean {
  return Boolean(
    partners?.some((partner) => partner.uuid === MARYVILLE_PARTNER_UUID),
  );
}

export default function FormProductHero({
  brandImage,
  courseDetails,
  courseImage,
  partner,
  path,
  productType,
  subheader,
  taxiForm,
  title,
  taxiFormData,
  price,
  objectId,
  partners,
}: DataProps) {
  const t = useTranslations("Common");
  const degree51Labels = {
    rankedByUsNews: t("rankedByUsNews"),
    online100: t("online100"),
    programType: t("programType"),
    flexibleDates: t("flexibleDates"),
    programDuration: t("programDuration"),
    learnOnYourSchedule: t("learnOnYourSchedule"),
    freeApplication: t("freeApplication"),
    applyForFreeToday: t("applyForFreeToday"),
    hassleFreeAdmissions: t("hassleFreeAdmissions"),
    noEntranceExamsAndCreditTransfer: t("noEntranceExamsAndCreditTransfer"),
  };
  // Find degree data by objectId or partner UUIDs
  const degree51Data = findCustomFactoidData(objectId, partners);

  // If course is in degreePageDesign51, use the mapped data
  if (degree51Data) {
    const extendedCourseDetails = processDegree51Data(
      degree51Data,
      courseDetails,
      degree51Labels,
    );

    return (
      <FormProductHeroCombined
        brandImage={brandImage}
        courseDetails={extendedCourseDetails}
        courseImage={courseImage}
        partner={partner}
        path={path}
        productType={productType}
        subheader={subheader}
        taxiForm={taxiForm}
        taxiFormData={taxiFormData}
        title={title}
        statsVariant={"light-logo"}
        hasUsNewsLogo={degree51Data.showUSNewLogo ?? false}
        colorBarHexValue={degree51Data.colorBarHexValue}
      />
    );
  }

  // Fallback to current behavior for courses not in degreePageDesign51
  const courseDetailsLength = courseDetails ? courseDetails.length : 0;
  return (
    <FeatureFlagComponentResolverClient
      flagKey="NEXT_PUBLIC_FEATURE_DEGREE_DETAIL_V51_HERO_STATS"
      variants={{
        legacy: (
          <FormProductHeroCombined
            brandImage={brandImage}
            courseDetails={courseDetails}
            courseImage={courseImage}
            partner={partner}
            path={path}
            productType={productType}
            subheader={subheader}
            taxiForm={taxiForm}
            taxiFormData={taxiFormData}
            title={title}
            statsVariant="transparent"
          />
        ),
        test: (
          <MonarchResolver<HeroStatsTestResult>
            experimentKey="useHeroStatsRedesign"
            variants={{
              control: (
                <FormProductHeroCombined
                  brandImage={brandImage}
                  courseDetails={courseDetails}
                  courseImage={courseImage}
                  partner={partner}
                  path={path}
                  productType={productType}
                  subheader={subheader}
                  taxiForm={taxiForm}
                  taxiFormData={taxiFormData}
                  title={title}
                  statsVariant="transparent"
                />
              ),
              test: (
                <MonarchDataConsumer<HeroStatsTestResult>
                  experimentKey="useHeroStatsRedesign"
                  render={(testResult) => {
                    if (!courseDetails || !testResult?.data) return null;
                    const extendedCourseDetails: ParsedFactoid[] =
                      courseDetails[0].description
                        ?.toString()
                        .includes("Ranked") || testResult.data.showUsNewsLogo
                        ? [
                            {
                              heading: testResult.data.rankingText
                                ? testResult.data.rankingText
                                : courseDetails[0].heading,
                              description:
                                testResult.data.rankingText &&
                                testResult.data.showUsNewsLogo
                                  ? degree51Labels.rankedByUsNews
                                  : courseDetails[0].description,
                              sortOrder: courseDetails[0].sortOrder,
                            },
                          ]
                        : [];
                    // Add tuition information
                    if (testResult.data.tuitionAndFees) {
                      extendedCourseDetails.push({
                        heading: testResult.data.tuitionAndFees,
                        description: t("tuitionAndFees"),
                        sortOrder: extendedCourseDetails.length,
                      });
                    } else {
                      if (testResult.data.tuitionAndFees !== null) {
                        extendedCourseDetails.unshift({
                          heading: price ?? t("variesByProgram"),
                          description: t("tuitionAndFees"),
                          sortOrder: 0,
                        });
                      }
                    }

                    // Add program type information
                    if (testResult.data.programType) {
                      extendedCourseDetails.push({
                        heading: testResult.data.programType,
                        description: degree51Labels.programType,
                        sortOrder: extendedCourseDetails.length,
                      });
                    }

                    // Add program dates information
                    if (testResult.data.programDates) {
                      extendedCourseDetails.push({
                        heading: degree51Labels.flexibleDates,
                        description: testResult.data.programDates,
                        sortOrder: extendedCourseDetails.length,
                      });
                    }

                    // Add program duration information
                    if (testResult.data.programDuration) {
                      extendedCourseDetails.push({
                        heading: testResult.data.programDuration,
                        description: testResult.data.programDuration
                          .toString()
                          .includes("Years")
                          ? degree51Labels.programDuration
                          : degree51Labels.learnOnYourSchedule,
                        sortOrder: extendedCourseDetails.length,
                      });
                    }

                    if (
                      !partnersIncludeMaryville(partners) &&
                      extendedCourseDetails.length <= 3 &&
                      courseDetails[courseDetailsLength - 1]
                    ) {
                      extendedCourseDetails.push({
                        heading: degree51Labels.freeApplication,
                        description: degree51Labels.applyForFreeToday,
                        sortOrder:
                          courseDetails[courseDetailsLength - 1].sortOrder,
                      });
                    }

                    return (
                      <FormProductHeroCombined
                        brandImage={brandImage}
                        courseDetails={
                          testResult.data.designVariant === "transparent" ||
                          testResult.data.designVariant === "default"
                            ? courseDetails
                            : extendedCourseDetails
                        }
                        courseImage={courseImage}
                        partner={partner}
                        path={path}
                        productType={productType}
                        subheader={subheader}
                        taxiForm={taxiForm}
                        taxiFormData={taxiFormData}
                        title={title}
                        statsVariant={testResult.data.designVariant}
                        hasUsNewsLogo={testResult.data.showUsNewsLogo}
                        colorBarHexValue={testResult.data.colorBarHexValue}
                      />
                    );
                  }}
                />
              ),
            }}
            fallbackVariant="control"
          />
        ),
      }}
      fallback="legacy"
    />
  );
}
